/** The user's published pages and games, with a short path from idea to link. */
import React, { useCallback, useRef, useState } from 'react';
import { View, StyleSheet, ScrollView, ActivityIndicator, Platform, Pressable, RefreshControl } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useRouter } from 'expo-router';
import { useSiteColors, SiteText as Text } from './siteTheme';
import { sitesApi } from '@/lib/api';
import { effectiveSiteStatus, slimPublishedSite, SITE_RENEWAL } from '@/lib/publishedSites';
import { openHomeComposer } from '@/lib/openHomeComposer';
import MetaHead from '@/components/MetaHead';
import Icon from '@/components/Icon';
import { PublishedSiteCard } from '@/components/Coworker/WorkPublishedSites';

function Action({ label, icon, onPress, colors }) {
  return <Pressable accessibilityRole="button" accessibilityLabel={label} onPress={onPress}
    style={({ pressed, hovered }) => [styles.action, { backgroundColor: colors.text_color_primary }, (pressed || hovered) && styles.pressed]}>
    {icon && <Icon name={icon} size={14} color={colors.solid_color_primary} />}
    <Text size="m" weight="bold" style={{ color: colors.solid_color_primary }}>{label}</Text>
  </Pressable>;
}

function SiteIllustration({ colors }) {
  return <View accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants"
    style={[styles.illustration, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_secondary }]}>
    <View style={[styles.browserBar, { borderBottomColor: colors.border_color_secondary }]}>
      {[0, 1, 2].map(dot => <View key={dot} style={[styles.browserDot, { backgroundColor: colors.text_color_secondary }]} />)}
    </View>
    <View style={styles.browserBody}><Icon name="globe" size={32} color={colors.text_color_secondary} /></View>
  </View>;
}

export default function PublishedSitesPage() {
  const colors = useSiteColors();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const request = useRef(0);
  const [guideOpen, setGuideOpen] = useState(false);
  const [state, setState] = useState({ loading: true, sites: [], limits: null, error: null });

  const load = useCallback(async () => {
    const id = ++request.current;
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const out = await sitesApi.list();
      if (id !== request.current) return;
      setState({ loading: false, sites: (out?.sites || []).map(slimPublishedSite).filter(Boolean), limits: out?.limits || null, error: null });
    } catch (error) {
      if (id === request.current) setState(s => ({ ...s, loading: false, error: error?.message || 'Could not load your sites.' }));
    }
  }, []);

  useFocusEffect(useCallback(() => {
    void load();
    return () => { request.current += 1; };
  }, [load]));

  const create = () => openHomeComposer(router, { work: true });
  const live = state.sites.filter(site => effectiveSiteStatus(site) === 'live').length;
  const hasSites = state.sites.length > 0;
  const mobile = Platform.OS === 'ios' || Platform.OS === 'android';

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.solid_color_primary }}
      contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 32 }]}
      refreshControl={mobile ? <RefreshControl refreshing={state.loading && hasSites} onRefresh={load} tintColor={colors.text_color_secondary} /> : undefined}>
      <MetaHead title="My Sites" />
      <View style={styles.header}>
        <View style={styles.headingCopy}>
          <Text tag="h1" size="xxxl" weight="bold" style={styles.title}>My Sites</Text>
          <Text size="m" color="secondary" style={styles.subtitle}>Your creations, out in the world.</Text>
        </View>
        {hasSites && <Action label="New" icon="plus" onPress={create} colors={colors} />}
      </View>

      {state.loading && !hasSites ? <View style={styles.loading} accessibilityLabel="Loading your sites" accessibilityRole="progressbar">
        <ActivityIndicator color={colors.text_color_secondary} />
      </View> : null}
      {state.error ? <View style={[styles.error, { backgroundColor: colors.solid_color_secondary }]}>
        <Text size="m" weight="bold" accessibilityRole="alert">Your sites couldn’t load</Text>
        <Text size="n" color="secondary" style={styles.body}>{state.error}</Text>
        <Action label="Try again" onPress={load} colors={colors} />
      </View> : null}
      {!state.loading && !state.error && !hasSites ? <View style={styles.empty}>
        <SiteIllustration colors={colors} />
        <Text tag="h2" size="xxl" weight="bold" style={styles.emptyTitle}>Publish your first site</Text>
        <Text size="m" color="secondary" style={styles.emptyCopy}>Create a page or game in Work,{'\n'}then share it with a link.</Text>
        <Action label="Create in Work" icon="plus" onPress={create} colors={colors} />
        <Text size="s" color="secondary" style={styles.publicNote}>Anyone can visit. No Gab AI account needed.</Text>
      </View> : null}
      {hasSites ? <View style={styles.collection}>
        <Text size="s" color="secondary">{live} live{state.sites.length > live ? ` · ${state.sites.length - live} offline` : ''}</Text>
        {state.sites.map(site => <PublishedSiteCard key={site.name} site={site} variant="library" onUnpublished={load} onRenewed={load} />)}
      </View> : null}

      <View style={[styles.helpSection, { borderTopColor: colors.border_color_secondary }]}>
        <Pressable onPress={() => setGuideOpen(open => !open)} accessibilityRole="button" accessibilityLabel="Learn how to publish and share"
          accessibilityState={{ expanded: guideOpen }}
          style={({ hovered, pressed }) => [styles.guide, (hovered || pressed) && styles.pressed]}>
          <View style={[styles.guideIcon, { backgroundColor: colors.solid_color_secondary }]}><Icon name="book-open" size={18} color={colors.text_color_primary} /></View>
          <View style={styles.guideCopy}>
            <Text size="m" weight="medium">Publishing & sharing</Text>
            <Text size="s" color="secondary" style={styles.guideSubtitle}>From your first idea to a public link.</Text>
          </View>
          <Icon name={guideOpen ? 'chevron-up' : 'chevron-down'} size={13} color={colors.text_color_secondary} />
        </Pressable>
        {guideOpen && <View style={styles.guideSteps}>
          {[
            ['Create in Work', 'Ask Work to build a page or browser game, or open one you’ve already made.'],
            ['Choose Publish site', 'Open the creation, choose Publish site, pick an address, and confirm. You can also ask Work to publish it in the chat.'],
            ['Share your link', 'Once it’s live, copy the link and send it to anyone. Manage expiration and unpublish your sites here.'],
          ].map(([title, copy], index) => <View key={title} style={styles.guideStep}>
            <View style={[styles.stepNumber, { backgroundColor: colors.solid_color_secondary }]}><Text size="s" weight="medium">{index + 1}</Text></View>
            <View style={styles.guideCopy}><Text size="m" weight="medium">{title}</Text><Text size="n" color="secondary" style={styles.body}>{copy}</Text></View>
          </View>)}
        </View>}
        {state.limits && <Text size="s" color="secondary" style={styles.planNote}>Your plan includes up to {state.limits.maxLive} live sites, online for {state.limits.ttlDays} days each. Renew any site to keep it online for {(state.limits.renewal || SITE_RENEWAL).days} more days for {(state.limits.renewal || SITE_RENEWAL).credits} credits.</Text>}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  content: { width: '100%', maxWidth: 760, alignSelf: 'center', paddingHorizontal: 24, paddingTop: 32, gap: 28 },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' },
  headingCopy: { gap: 8, flexShrink: 1 },
  title: { letterSpacing: -0.7, lineHeight: 36 },
  subtitle: { lineHeight: 23 },
  loading: { minHeight: 320, alignItems: 'center', justifyContent: 'center' },
  empty: { alignItems: 'center', paddingVertical: 32 },
  illustration: { width: 136, height: 104, borderWidth: 1, borderRadius: 16, overflow: 'hidden', marginBottom: 28 },
  browserBar: { height: 25, flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, borderBottomWidth: 1 },
  browserDot: { width: 4, height: 4, borderRadius: 2, opacity: 0.7 },
  browserBody: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  emptyTitle: { textAlign: 'center', letterSpacing: -0.4, lineHeight: 30, marginBottom: 12 },
  emptyCopy: { textAlign: 'center', maxWidth: 300, lineHeight: 23, marginBottom: 24 },
  publicNote: { textAlign: 'center', lineHeight: 20, marginTop: 20, maxWidth: 270 },
  action: { minHeight: 48, borderRadius: 12, paddingHorizontal: 20, paddingVertical: 12, flexDirection: 'row', gap: 8, alignItems: 'center', justifyContent: 'center', alignSelf: 'center' },
  pressed: { opacity: 0.7 },
  error: { alignItems: 'flex-start', padding: 24, borderRadius: 16, gap: 16 },
  body: { lineHeight: 21 },
  collection: { gap: 16 },
  helpSection: { borderTopWidth: StyleSheet.hairlineWidth, paddingTop: 16, gap: 16 },
  guide: { flexDirection: 'row', alignItems: 'center', gap: 14, minHeight: 64, paddingVertical: 8 },
  guideIcon: { width: 44, height: 44, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  guideCopy: { flex: 1, minWidth: 0, gap: 5 },
  guideSubtitle: { lineHeight: 19 },
  guideSteps: { gap: 24, paddingTop: 8, paddingBottom: 12 },
  guideStep: { flexDirection: 'row', alignItems: 'flex-start', gap: 14 },
  stepNumber: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  planNote: { lineHeight: 20 },
});
